import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, updateData } from '../../services/request'

const AdminReminders = () => {
  const [config, setConfig] = useState({
    reminder3DayEnabled: true,
    reminder7DayEnabled: true
  })
  const [notifConfig, setNotifConfig] = useState({ emailEnabled: true, smsEnabled: true })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const fetchConfig = async () => {
    setLoading(true)
    setError('')
    const [reminderRes, notifRes] = await Promise.all([
      getData(`${getAPIBaseURL()}/employme/admin/reminders/config`, {}, {}),
      getData(`${getAPIBaseURL()}/employme/admin/notifications/config`, {}, {})
    ])
    setLoading(false)
    if (reminderRes.error === '' && reminderRes.result?.status === 200) {
      const data = reminderRes.result.data?.data || reminderRes.result.data
      setConfig({
        reminder3DayEnabled: data?.reminder3DayEnabled !== false,
        reminder7DayEnabled: data?.reminder7DayEnabled !== false
      })
    } else {
      setError(reminderRes.result?.data?.message || reminderRes.result?.data?.data || 'Unable to load config')
    }
    if (notifRes.error === '' && notifRes.result?.status === 200) {
      const data = notifRes.result.data?.data || notifRes.result.data
      setNotifConfig({
        emailEnabled: data?.emailEnabled !== false,
        smsEnabled: data?.smsEnabled !== false
      })
    }
  }

  useEffect(() => {
    fetchConfig()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    setSuccess('')
    const headers = { 'Content-Type': 'application/json' }
    const [reminderRes, notifRes] = await Promise.all([
      updateData(`${getAPIBaseURL()}/employme/admin/reminders/config`, headers, config),
      updateData(`${getAPIBaseURL()}/employme/admin/notifications/config`, headers, notifConfig)
    ])
    setSaving(false)
    const failed = reminderRes.error || notifRes.error
    if (!failed && reminderRes.result?.status === 200 && notifRes.result?.status === 200) {
      setSuccess('Settings saved successfully')
      if (reminderRes.result?.data?.data) setConfig(reminderRes.result.data.data)
      if (notifRes.result?.data?.data) setNotifConfig(notifRes.result.data.data)
    } else {
      setError(reminderRes.result?.data?.message || notifRes.result?.data?.data || 'Failed to save')
    }
  }

  return (
    <section className="mx-auto max-w-2xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reminders</h1>
          <p className="text-sm text-gray-600">Configure 3-day and 7-day unpaid job reminders for employers</p>
        </div>
        <Link
          to="/admin/app/employme"
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Back to overview
        </Link>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {success && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
              <div>
                <h3 className="font-semibold text-gray-900">3-day unpaid job reminder</h3>
                <p className="text-sm text-gray-600">
                  Send reminder to employers who have draft jobs unpaid for 3 days
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.reminder3DayEnabled}
                  onChange={(e) => setConfig((prev) => ({ ...prev, reminder3DayEnabled: e.target.checked }))}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-gray-800 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-gray-300"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {config.reminder3DayEnabled ? 'On' : 'Off'}
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-gray-200 p-4">
              <div>
                <h3 className="font-semibold text-gray-900">7-day unpaid job reminder</h3>
                <p className="text-sm text-gray-600">
                  Send reminder to employers who have draft jobs unpaid for 7 days
                </p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  checked={config.reminder7DayEnabled}
                  onChange={(e) => setConfig((prev) => ({ ...prev, reminder7DayEnabled: e.target.checked }))}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-gray-800 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-4 peer-focus:ring-gray-300"></div>
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {config.reminder7DayEnabled ? 'On' : 'Off'}
                </span>
              </label>
            </div>

            <h3 className="pt-4 text-sm font-semibold text-gray-900">Notification channels</h3>
            <p className="text-xs text-gray-600">Enable or disable email and SMS notifications globally</p>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={notifConfig.emailEnabled}
                  onChange={(e) => setNotifConfig((prev) => ({ ...prev, emailEnabled: e.target.checked }))}
                />
                <span className="text-sm">Email</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={notifConfig.smsEnabled}
                  onChange={(e) => setNotifConfig((prev) => ({ ...prev, smsEnabled: e.target.checked }))}
                />
                <span className="text-sm">SMS</span>
              </label>
            </div>

            <p className="text-xs text-gray-500">
              Reminder templates can be managed in the Templates section. Ensure you have templates configured for
              unpaid job reminders.
            </p>

            <button
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save settings'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminReminders
